
import { defineComponent, onMounted, ref } from 'vue';
import PageContentBase from '@/layout/_shared/PageContentBase.vue';
import PageContentSection from '@/layout/_shared/PageContentSection.vue';
import DivRow from '@/layout/_shared/DivRow.vue';
import DivCol from '@/layout/_shared/DivCol.vue';
import ButtonCustom from '@/components/shared/buttons/ButtonCustom.vue';
import LabelCustom from '@/components/shared/label-custom/LabelCustom.vue';
import AvatarUploader from '@/components/shared/avatar-uploader/AvatarUploader.vue';
import { useRouterCustom } from '@/core/hooks/use-router-custom';
import { useButtonCustom } from '@/core/hooks/use-button-custom';
import { useLoading } from '@/core/hooks/use-loading';
import useFormInput from '@/core/hooks/use-form-input';
import {
  setBreadcrumbLinks,
  setCurrentPageTitle,
} from '@/core/helpers/breadcrumb';
import {
  lengthValidationRules,
  swalErrNotification,
  swalNotification,
} from '@/core/helpers/utils';
import { PHONE_NUMBER_REGEX } from '@/core/constants/const';
import { ProfileService } from '@/core/services/ProfileService';
import { resolveAvatarUrl } from '@/core/helpers/avatar';
import BoPhanService from '@/core/services/BoPhan.service';
import ChucVuService from '@/core/services/ChucVu.service';
import type {
  IMeProfile,
  INguoiDungProfileUpdatePayload,
  PartBaseResponse,
  PositionBaseResponse,
} from '@/core/interfaces/ApiResponses';

export default defineComponent({
  name: 'chinh-sua-thong-tin-nguoi-dung',

  components: {
    AvatarUploader,
    ButtonCustom,
    DivCol,
    DivRow,
    LabelCustom,
    PageContentBase,
    PageContentSection,
  },

  setup() {
    const { push } = useRouterCustom();
    const { ButtonsType, ButtonTypeColors } = useButtonCustom();
    const { isLoading, startLoading, endLoading } = useLoading();
    const { focusFirstErrInputV2 } = useFormInput();

    const formRef = ref();
    const pageLoading = ref(false);
    const avatarUrl = ref('');
    const username = ref('');
    const departmentName = ref('—');
    const boPhanOptions = ref<PartBaseResponse[]>([]);
    const chucVuOptions = ref<PositionBaseResponse[]>([]);

    const form = ref({
      ho_va_ten: '',
      so_dien_thoai: '',
      email: '',
      id_bo_phan: undefined as number | undefined,
      id_chuc_vu: undefined as number | undefined,
    });

    const rules = {
      ho_va_ten: lengthValidationRules({
        name: 'Họ và tên',
        min: 1,
        max: 255,
      }),
      so_dien_thoai: [
        ...lengthValidationRules({
          name: 'Số điện thoại',
          min: 1,
          max: 15,
        }),
        {
          pattern: PHONE_NUMBER_REGEX,
          message: 'Số điện thoại không hợp lệ',
          trigger: 'blur',
        },
      ],
      email: [
        {
          type: 'email',
          message: 'Email không hợp lệ',
          trigger: 'blur',
        },
      ],
    };

    const ensureOption = <T extends { id: number }>(
      options: T[],
      id: number | undefined,
      fallback: T | null
    ): T[] => {
      if (!id || id <= 0 || !fallback) {
        return options;
      }
      if (options.some((item) => item.id === id)) {
        return options;
      }
      return [fallback, ...options];
    };

    const hydrateFromProfile = (profile: IMeProfile) => {
      form.value = {
        ho_va_ten: profile.ho_va_ten || '',
        so_dien_thoai: profile.so_dien_thoai || '',
        email: profile.email || '',
        id_bo_phan: profile.id_bo_phan > 0 ? profile.id_bo_phan : undefined,
        id_chuc_vu: profile.id_chuc_vu > 0 ? profile.id_chuc_vu : undefined,
      };
      avatarUrl.value = resolveAvatarUrl(
        profile.anh_dai_dien,
        profile.anh_dai_dien_url
      );
      username.value = profile.ten_dang_nhap || '';
      departmentName.value =
        profile.don_vi?.ten_don_vi ||
        profile.don_vi?.ma_don_vi ||
        '—';

      // Keep orphan assigned values visible even if missing from catalog.
      if (profile.bo_phan) {
        boPhanOptions.value = ensureOption(
          boPhanOptions.value,
          form.value.id_bo_phan,
          {
            id: profile.bo_phan.id,
            ten_bo_phan: profile.bo_phan.ten_bo_phan,
            ma_bo_phan: profile.bo_phan.ma_bo_phan,
            mo_ta: profile.bo_phan.mo_ta ?? null,
          }
        );
      }
      if (profile.chuc_vu) {
        chucVuOptions.value = ensureOption(
          chucVuOptions.value,
          form.value.id_chuc_vu,
          {
            id: profile.chuc_vu.id,
            ten_chuc_vu: profile.chuc_vu.ten_chuc_vu,
            ten_viet_tat: profile.chuc_vu.ten_viet_tat || '',
          }
        );
      }
    };

    const onAvatarUploaded = (profile: IMeProfile) => {
      hydrateFromProfile(profile);
    };

    const loadCatalogOptions = async () => {
      const [boPhanList, chucVuList] = await Promise.all([
        BoPhanService.listForSelect(),
        ChucVuService.listForSelect(),
      ]);
      boPhanOptions.value = boPhanList;
      chucVuOptions.value = chucVuList;
    };

    const loadProfile = async () => {
      try {
        pageLoading.value = true;
        // Docs: GET /me/profile + catalog lists in parallel
        const [{ data: profileRes }] = await Promise.all([
          ProfileService.getProfile(),
          loadCatalogOptions(),
        ]);
        hydrateFromProfile(profileRes.data);
        ProfileService.applyProfileToStore(profileRes.data);
      } catch (error) {
        await swalErrNotification(
          error,
          'Không thể tải hồ sơ, vui lòng thử lại'
        );
      } finally {
        pageLoading.value = false;
      }
    };

    const buildUpdatePayload = (): INguoiDungProfileUpdatePayload => {
      const payload: INguoiDungProfileUpdatePayload = {
        ho_va_ten: form.value.ho_va_ten.trim(),
        so_dien_thoai: form.value.so_dien_thoai.trim(),
        email: form.value.email?.trim() || null,
      };

      // FK fields: only send integers >= 1 (never 0)
      if (form.value.id_bo_phan != null && form.value.id_bo_phan >= 1) {
        payload.id_bo_phan = form.value.id_bo_phan;
      }
      if (form.value.id_chuc_vu != null && form.value.id_chuc_vu >= 1) {
        payload.id_chuc_vu = form.value.id_chuc_vu;
      }

      return payload;
    };

    const submit = () => {
      if (!formRef.value) {
        return;
      }

      formRef.value.validate(async (valid: boolean) => {
        if (!valid) {
          focusFirstErrInputV2();
          return;
        }

        try {
          startLoading();
          const {
            data: { data },
          } = await ProfileService.updateProfile(buildUpdatePayload());
          ProfileService.applyProfileToStore(data);
          hydrateFromProfile(data);
          await swalNotification('Cập nhật hồ sơ thành công', 'success');
          push({ name: 'thong-tin-nguoi-dung-hien-tai' });
        } catch (error) {
          await swalErrNotification(
            error,
            'Không thể cập nhật hồ sơ, vui lòng thử lại'
          );
        } finally {
          endLoading();
        }
      });
    };

    onMounted(async () => {
      setCurrentPageTitle('Cập nhật hồ sơ');
      setBreadcrumbLinks([
        {
          text: 'Tài khoản của tôi',
          routeData: {
            name: 'thong-tin-nguoi-dung-hien-tai',
          },
        },
        {
          text: 'Chỉnh sửa',
        },
      ]);

      await loadProfile();
    });

    return {
      ButtonTypeColors,
      ButtonsType,
      avatarUrl,
      boPhanOptions,
      chucVuOptions,
      departmentName,
      form,
      formRef,
      isLoading,
      onAvatarUploaded,
      pageLoading,
      rules,
      submit,
      username,
    };
  },
});
